import { parse } from 'querystringify'
import { useParams, useLocation } from 'react-router-dom'

// Actions
import { actions as browseActions } from 'modules/browse/actions'

// Components
import { ContentHeader } from 'kit'
import { Error, Spinner, Layout, Image } from '@tracklib/kit'
import { SongFilter } from 'components/filter'
import Artists, { ArtistsAsString } from 'components/track/artists'
import CoverArt from 'components/coverart'
import InfoItem2 from 'components/base/infoitem2'
import SEO from 'components/base/seo'
import TrackList from 'components/browse/tracklist'

// Hooks
import { useSearchQuery } from 'modules/browse/hooks'
import { useSongs, useTracks, useLabels, useGenres, useRegions, useArtists } from 'modules/entities/hooks'

// Types
import { Dispatch } from 'types'
import { createSearchQuery } from 'pages/collection'

// Utils
import { getLoopFromLocation } from 'utils'
import { CDN_ROOT } from '@constants'

import styles from './song.css'

export const loadInitialData =
  ({
    location: {
      params: { slug },
    },
  }: {
    location: { params: { slug: string } }
  }) =>
  async (dispatch: Dispatch) => {
    const searchQuery = createSearchQuery({ song: slug }, '')
    return dispatch(browseActions.fetchTracksBySearchQuery(searchQuery))
  }

const SongPage = () => {
  const { slug = '' } = useParams() as { slug: string }
  const { search } = useLocation()
  const query = (search ? parse(search) : {}) as { shared_loop: boolean }
  const hasLoop = !!query.shared_loop

  const { request, trackIds, ...trackListProps } = useSearchQuery({ song: slug }, slug)

  const allSongs = useSongs()
  const allTracks = useTracks()
  const allLabels = useLabels()
  const allGenres = useGenres()
  const allArtists = useArtists()
  const allRegions = useRegions()

  const song = Object.keys(allSongs || {})
    .map(id => allSongs[id])
    .find(x => x.slug === slug)

  const tracks = trackIds?.map(({ id }) => {
    const track = allTracks[String(id)]
    return {
      id,
      loop: getLoopFromLocation(search, track),
      track,
    }
  })

  if (!song || !request) {
    if (request.isLoading) {
      return <Spinner minHeight={400} />
    } else if (request.isDone) {
      return <Error message="Song couldn't be found" />
    }
    return null
  }

  const { regions: regionIds, labels: labelIds, genres: genreIds, tonal, licenseinfo, artists: artistIds } = song
  const labels = labelIds && labelIds.map(labelId => allLabels[String(labelId)])
  const regions = regionIds && regionIds.map(regionId => allRegions[String(regionId)])
  const genres = genreIds && genreIds.map(genreId => allGenres[String(genreId)])
  const artists = artistIds && artistIds.map(artistId => allArtists[String(artistId)])
  const year = song.release_year
  const mainArtists = artists.filter(x => x.type === 'Main Artist')
  const breadcrumbs = mainArtists?.length
    ? [
        {
          text: 'Artists',
          link: '/artists/',
        },
        mainArtists.map(x => ({ text: x.name, link: `/artist/${x.slug}` })).splice(0, 6),
        {
          text: song.title,
          link: `/song/${song.slug}`,
        },
      ]
    : null

  const description = `${hasLoop ? 'Loop of ' : ''}${song.title} by ${ArtistsAsString(artists, song.genres)} | Sample this song on Tracklib`
  const image = song.cover ? song.cover : '/static/images/og_image.jpg'

  return (
    <>
      <SEO
        title={`${hasLoop ? 'Loop of ' : ''}${song.title} by ${mainArtists.map(artist => artist.name).join(', ')} | Sample on Tracklib`}
        description={description}
        image={Image.getSrc(image, 260, 260, 'jpg', true, CDN_ROOT)}
        imageWidth={260}
        imageHeight={260}
        type="music.song"
      >
        <meta name="twitter:label1" content="Release year" />
        <meta name="twitter:data1" content={String(year)} />
        <meta name="twitter:label2" content="Get a free trial" />
        <meta name="twitter:data2" content="https://tracklib.com/subscribe" />
      </SEO>
      <Layout.FullWidth>
        <ContentHeader
          image={<CoverArt cover={song.cover} title={song.title} size="auto" />}
          type="Song"
          title={song.title}
          by={<Artists artists={song.artists} genres={song.genres} />}
          infoItems={[
            ...(year ? [<InfoItem2 year={year} />] : []),
            ...(genres?.length > 0 ? [<InfoItem2 genres={genres} />] : []),
            ...(tonal ? [<InfoItem2 tonal={tonal} />] : []),
            ...(labels?.length > 0 ? [<InfoItem2 labels={labels} />] : []),
            ...(regions?.length > 0 ? [<InfoItem2 regions={regions} />] : []),
            ...[<InfoItem2 licenseInfo={licenseinfo} />],
          ]}
          sharePath={`/song/${song.slug}/`}
          shareCampaign="share-song"
          adminEditPath={`/tracks/song/${song.id}/change`}
          breadcrumbs={breadcrumbs}
          className={styles.contentWrapper}
        />
        <SongFilter />
        <TrackList {...trackListProps} trackIds={tracks} request={request} renderSpecificTrackNames renderOnlySpecificTrackName />
      </Layout.FullWidth>
    </>
  )
}

export default SongPage
